/**
 * Accès à l'application de bureau, depuis l'interface commune.
 *
 * Ce paquet est servi tel quel sur le web et emballé tel quel par Electron.
 * Le pont `window.diagflow` n'existe que dans le second cas : c'est le seul
 * signal, et tout ce que le mode hors ligne ajoute à l'écran passe par
 * `isDesktop()`. Dans le navigateur, ces écrans n'existent pas — ils n'y
 * auraient aucun sens, et un bouton « emporter les rapports » y serait un
 * bouton mort.
 *
 * Aucune règle métier ne vit ici : le pont ne fait que porter des appels.
 */

export type ApiResult = { status: number; body: unknown };

export type ConnectionState = 'aJour' | 'synchronisation' | 'horsLigne';

export type LiveState = 'connecte' | 'connexion' | 'interrompu';

export type SyncStatus = {
  state: ConnectionState;
  pending: number;
  failed: number;
  lastSyncAt: string | null;
  lastError: string | null;
  authenticated: boolean;
  /** État du canal de notification, distinct de celui de la synchronisation. */
  live: LiveState;
};

/** Message transmis par le processus principal : un événement, ou un état. */
export type RealtimeMessage =
  | { kind: 'evenement'; at: string; event: unknown }
  | { kind: 'etat'; state: LiveState };

export type OutboxEntry = {
  id: string;
  workstationId: string;
  type: string;
  occurredAt: string;
  reason: string;
  payload: Record<string, unknown>;
  createdAt: string;
  status: 'enAttente' | 'echec';
  attempts: number;
  lastAttemptAt: string | null;
  failureStatus: number | null;
  failureCode: string | null;
  failureReason: string | null;
};

export type FollowedWorkstation = {
  workstationId: string;
  name: string;
  followedAt: string;
  fileCount: number;
  missingCount: number;
  sizeBytes: number;
};

export type StorageUsage = {
  workstations: FollowedWorkstation[];
  orphanCount: number;
  orphanSizeBytes: number;
  totalFiles: number;
  totalSizeBytes: number;
};

export type FreedSpace = { removedFiles: number; freedBytes: number };

export type DesktopSettings = { serverUrl: string; timeZone: string };

export type DesktopBridge = {
  api(method: string, path: string, body?: unknown): Promise<ApiResult>;
  sync: {
    status(): Promise<SyncStatus>;
    now(): Promise<SyncStatus>;
    subscribe(listener: (status: SyncStatus) => void): () => void;
  };
  realtime: {
    status(): Promise<{ state: LiveState }>;
    subscribe(listener: (message: RealtimeMessage) => void): () => void;
  };
  outbox: {
    list(): Promise<{ pending: OutboxEntry[]; failed: OutboxEntry[] }>;
    retry(id: string): Promise<{ pending: number; failed: number }>;
    discard(id: string): Promise<{ pending: number; failed: number }>;
  };
  reports: {
    usage(): Promise<StorageUsage>;
    isFollowed(workstationId: string): Promise<boolean>;
    hasFile(diagnosticId: string): Promise<boolean>;
    follow(workstationId: string): Promise<StorageUsage>;
    unfollow(workstationId: string): Promise<{ freed: FreedSpace; usage: StorageUsage }>;
    freeAll(): Promise<{ freed: FreedSpace; usage: StorageUsage }>;
  };
  settings: {
    read(): Promise<DesktopSettings>;
    write(patch: Partial<DesktopSettings>): Promise<DesktopSettings>;
  };
};

declare global {
  interface Window {
    diagflow?: DesktopBridge;
  }
}

export function isDesktop(): boolean {
  return typeof window !== 'undefined' && window.diagflow !== undefined;
}

/**
 * Pont garanti présent. À n'appeler que sous une garde `isDesktop()` : lever
 * ici vaut mieux que rendre un objet factice, qui ferait échouer plus loin
 * sans dire pourquoi.
 */
export function desktop(): DesktopBridge {
  const bridge = window.diagflow;
  if (bridge === undefined) {
    throw new Error("Cette fonction n'existe que dans l'application de bureau.");
  }
  return bridge;
}

export const CONNECTION_LABELS: Record<ConnectionState, string> = {
  aJour: 'À jour',
  synchronisation: 'Synchronisation…',
  horsLigne: 'Hors ligne',
};
